import { useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Archive,
  ArchiveRestore,
  ArrowUpRight,
  ListTree,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Dialog } from '@/components/dialog';
import { DataList, DataListRow } from '@/components/data-list';
import { EmptyState } from '@/components/empty-state';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { Textarea } from '@/components/textarea';
import { StatusPill } from '@/components/status-dot';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useAccountLedger,
  useAccounts,
  useCreateAccount,
  useDeleteAccount,
  useUpdateAccount,
  type AccountInput,
} from '@/lib/accounting';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import { dollars, formatDateFull } from '@/lib/format';
import { useFormError } from '@/lib/form-error';
import type { AccountData } from '@/types/api';

const TYPES: AccountInput['type'][] = [
  'asset',
  'liability',
  'equity',
  'revenue',
  'expense',
  'other_income',
];
const TYPE_LABEL: Record<string, string> = {
  asset: 'Asset',
  liability: 'Liability',
  equity: 'Equity',
  revenue: 'Revenue',
  expense: 'Expense',
  other_income: 'Other income',
};
const TYPE_ORDER: Record<string, number> = {
  asset: 0,
  liability: 1,
  equity: 2,
  revenue: 3,
  other_income: 4,
  expense: 5,
};

export function AccountsTab() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const { data, isLoading } = useAccounts(includeArchived);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [ledgerId, setLedgerId] = useState<number | null>(null);

  const list = data ?? [];
  const editing = editingId != null ? list.find((a) => a.id === editingId) ?? null : null;

  const grouped = useMemo(() => {
    const byType: Record<string, AccountData[]> = {};
    for (const a of list) {
      (byType[a.type] ??= []).push(a);
    }
    return Object.entries(byType).sort(([a], [b]) => (TYPE_ORDER[a] ?? 99) - (TYPE_ORDER[b] ?? 99));
  }, [list]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-2">
        <label className="inline-flex items-center gap-2 text-sm text-[var(--color-foreground-muted)]">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
          />
          Include archived
        </label>
        <Button onClick={() => setCreating(true)}>
          <Plus className="size-4" /> New account
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
      ) : list.length === 0 ? (
        <EmptyState
          icon={ListTree}
          title="No accounts"
          description="Seed the chart of accounts or add one manually."
          action={
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-4" /> New account
            </Button>
          }
        />
      ) : (
        <div className="space-y-5">
          {grouped.map(([type, rows]) => (
            <section key={type} className="space-y-2">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-foreground-subtle)]">
                {TYPE_LABEL[type] ?? type}
              </div>
              <DataList>
                {rows
                  .slice()
                  .sort((a, b) => a.code.localeCompare(b.code))
                  .map((a) => (
                    <AccountRow
                      key={a.id}
                      account={a}
                      onEdit={() => setEditingId(a.id)}
                      onOpen={() => setLedgerId(a.id)}
                    />
                  ))}
              </DataList>
            </section>
          ))}
        </div>
      )}

      <AccountDialog
        open={creating}
        onClose={() => setCreating(false)}
      />
      <AccountDialog
        account={editing}
        onClose={() => setEditingId(null)}
      />
      <AccountLedgerDialog accountId={ledgerId} onClose={() => setLedgerId(null)} />
    </div>
  );
}

function AccountRow({
  account,
  onEdit,
  onOpen,
}: {
  account: AccountData;
  onEdit: () => void;
  onOpen: () => void;
}) {
  const update = useUpdateAccount();
  const del = useDeleteAccount();
  const confirm = useConfirm();
  const toast = useToast();
  const isArchived = account.archived_at != null;

  return (
    <DataListRow onClick={onOpen}>
      <span className="w-16 shrink-0 font-mono text-xs text-[var(--color-foreground-subtle)] tabular-nums">
        {account.code}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="truncate font-medium text-foreground">{account.name}</span>
          {account.schedule_c_line ? (
            <Badge tone="info">Sch C {account.schedule_c_line}</Badge>
          ) : null}
          {isArchived ? <StatusPill tone="neutral">Archived</StatusPill> : null}
        </div>
        {account.description ? (
          <div className="mt-0.5 truncate text-xs text-muted-foreground">{account.description}</div>
        ) : null}
      </div>
      <span className="w-32 shrink-0 text-right font-medium tabular-nums">
        {dollars(account.balance_cents)}
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger
          onClick={(e) => e.stopPropagation()}
          className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus:opacity-100 group-hover/row:opacity-100 aria-expanded:opacity-100"
          aria-label={`Actions for ${account.name}`}
        >
          <MoreHorizontal className="size-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuItem onClick={onEdit}>
            <Pencil className="size-3.5" /> Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={async () => {
              await update.mutateAsync({ id: account.id, patch: { archived: !isArchived } });
              toast.success(`${account.name} ${isArchived ? 'unarchived' : 'archived'}`);
            }}
          >
            {isArchived ? (
              <>
                <ArchiveRestore className="size-3.5" /> Unarchive
              </>
            ) : (
              <>
                <Archive className="size-3.5" /> Archive
              </>
            )}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={async () => {
              const ok = await confirm({
                title: `Delete ${account.name}?`,
                body: 'Only allowed if the account has no journal activity.',
                confirmLabel: 'Delete',
                tone: 'danger',
              });
              if (ok) {
                try {
                  await del.mutateAsync(account.id);
                  toast.success(`${account.name} deleted`);
                } catch (err) {
                  const msg =
                    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
                    'Could not delete this account.';
                  toast.error('Could not delete', msg);
                }
              }
            }}
          >
            <Trash2 className="size-3.5" /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </DataListRow>
  );
}

/**
 * Drill-down into a single account: every posted posting that hit it, oldest
 * first, with a running balance. Each row jumps to its journal entry.
 */
function AccountLedgerDialog({
  accountId,
  onClose,
}: {
  accountId: number | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useAccountLedger(accountId);
  const [, setSearchParams] = useSearchParams();
  const account = data?.account;

  function openEntry(entryId: number) {
    setSearchParams({ tab: 'journal', focus: String(entryId), status: 'posted' });
  }

  return (
    <Dialog
      open={accountId != null}
      onClose={onClose}
      title={account ? `${account.code} · ${account.name}` : 'Account ledger'}
      size="xl"
    >
      {isLoading || !data ? (
        <p className="py-6 text-sm text-[var(--color-foreground-muted)]">Loading…</p>
      ) : data.rows.length === 0 ? (
        <EmptyState
          icon={ListTree}
          title="No posted activity"
          description="Nothing has posted to this account yet. Post a journal entry or a bank transaction and it will show up here."
        />
      ) : (
        <div className="space-y-3">
          <div className="max-h-[65vh] overflow-auto rounded-md border border-[var(--color-border)]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[var(--color-muted)] text-[11px] font-semibold uppercase tracking-wider text-[var(--color-foreground-subtle)]">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Date</th>
                  <th className="px-3 py-2 text-left font-semibold">Description</th>
                  <th className="px-3 py-2 text-right font-semibold">Debit</th>
                  <th className="px-3 py-2 text-right font-semibold">Credit</th>
                  <th className="px-3 py-2 text-right font-semibold">Balance</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, i) => (
                  <tr
                    key={`${row.journal_entry_id}-${i}`}
                    onClick={() => openEntry(row.journal_entry_id)}
                    className="group cursor-pointer border-t border-[var(--color-border)] transition-colors hover:bg-[var(--color-muted)]/40"
                  >
                    <td className="whitespace-nowrap px-3 py-2 text-[var(--color-foreground-subtle)]">
                      {formatDateFull(row.entry_date)}
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1">
                        <span className="text-foreground">{row.description}</span>
                        <ArrowUpRight className="size-3 shrink-0 text-transparent transition-colors group-hover:text-[var(--color-foreground-muted)]" />
                      </span>
                      {row.memo ? (
                        <span className="ml-1 text-xs text-muted-foreground">— {row.memo}</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {row.debit_cents > 0 ? dollars(row.debit_cents) : ''}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {row.credit_cents > 0 ? dollars(row.credit_cents) : ''}
                    </td>
                    <td className="px-3 py-2 text-right font-medium tabular-nums">
                      {dollars(row.running_balance_cents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-right text-sm">
            <span className="text-[var(--color-foreground-muted)]">Balance </span>
            <span className="font-semibold tabular-nums">{dollars(account?.balance_cents ?? 0)}</span>
          </p>
        </div>
      )}
    </Dialog>
  );
}

function AccountDialog({
  account,
  open,
  onClose,
}: {
  account?: AccountData | null;
  open?: boolean;
  onClose: () => void;
}) {
  const isEdit = !!account;
  const isOpen = isEdit ? account != null : !!open;
  const create = useCreateAccount();
  const update = useUpdateAccount();
  const { error, wrap } = useFormError();

  const [seeded, setSeeded] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState<AccountInput>(initial());

  const seedKey: number | 'new' | null = account ? account.id : isOpen ? 'new' : null;
  if (seedKey !== null && seeded !== seedKey) {
    setForm(account ? fromAccount(account) : initial());
    setSeeded(seedKey);
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const ok = await wrap(() =>
      account
        ? update.mutateAsync({ id: account.id, patch: form })
        : create.mutateAsync(form),
    );
    if (ok) {
      setSeeded(null);
      onClose();
    }
  }

  function handleClose() {
    setSeeded(null);
    onClose();
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      title={isEdit ? 'Edit account' : 'New account'}
      size="md"
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Code</span>
            <Input
              placeholder="6195"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              required
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Type</span>
            <Select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value as AccountInput['type'] })}
            >
              {TYPES.map((v) => (
                <option key={v} value={v}>
                  {TYPE_LABEL[v]}
                </option>
              ))}
            </Select>
          </label>
        </div>
        <label className="block space-y-1 text-sm">
          <span className="font-medium text-[var(--color-foreground-muted)]">Name</span>
          <Input
            placeholder="Continuing Education"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="font-medium text-[var(--color-foreground-muted)]">
            Schedule C line
          </span>
          <Input
            placeholder="e.g. 8, 20a, 27b"
            value={form.schedule_c_line ?? ''}
            onChange={(e) => setForm({ ...form, schedule_c_line: e.target.value || null })}
          />
          <span className="block text-[11px] text-[var(--color-foreground-subtle)]">
            Map this account to a Schedule C line so it's included in the year-end summary.
          </span>
        </label>
        <label className="block space-y-1 text-sm">
          <span className="font-medium text-[var(--color-foreground-muted)]">Description</span>
          <Textarea
            rows={3}
            value={form.description ?? ''}
            onChange={(e) => setForm({ ...form, description: e.target.value || null })}
          />
        </label>
        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending || update.isPending}>
            {create.isPending || update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function initial(): AccountInput {
  return {
    code: '',
    name: '',
    type: 'expense',
    schedule_c_line: null,
    description: null,
  };
}

function fromAccount(a: AccountData): AccountInput {
  return {
    code: a.code,
    name: a.name,
    type: a.type as AccountInput['type'],
    schedule_c_line: a.schedule_c_line,
    description: a.description,
  };
}
